import { ExternalLink, MoreHorizontal, Sparkles, User } from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

import { Button } from "@/components/ui/button";
import { GROW_EVENTS } from "@/constants/analyticsEvents";
import { ProfileSelector } from "./ProfileSelector";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { useMixpanel } from "@/hooks/useMixpanel";

interface SocialAccount {
  id: string;
  platform: "instagram" | "youtube" | "tiktok";
  handle: string;
  followers: number;
  posts: number;
}

interface GrowSidebarProps {
  socialAccounts: SocialAccount[];
  selectedAccount: string;
  onAccountChange: (accountId: string) => void;
  hasConnectedAccounts: boolean;
}

export const GrowSidebar = ({
  socialAccounts,
  selectedAccount,
  onAccountChange,
  hasConnectedAccounts,
}: GrowSidebarProps) => {
  const location = useLocation();
  const { track } = useMixpanel();

  const isProfileActive = location.pathname === "/grow/profile";
  const isContentProducerActive =
    location.pathname === "/grow/content-producer";

  return (
    <Sidebar variant="sidebar" collapsible="none" className="max-w-[300px]">
      <SidebarHeader className="p-6 border-b">
        <div className="mb-4">
          <Link to="/grow">
            <h2 className="text-lg font-semibold text-primary">
              CreatorGrid Grow Studio
            </h2>
          </Link>
        </div>

        <div className="flex gap-2 items-center">
          <ProfileSelector
            socialAccounts={socialAccounts}
            selectedAccount={selectedAccount}
            onAccountChange={onAccountChange}
            hasConnectedAccounts={hasConnectedAccounts}
          />
          <ThemeSwitcher triggerClass="h-10 bg-background" />
        </div>
      </SidebarHeader>

      <SidebarContent className="p-4">
        {/* Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium text-primary/60 uppercase tracking-wide">
            Tools
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isProfileActive}
                  className={isProfileActive ? "bg-muted" : ""}
                >
                  <Link to="/grow/profile">
                    <User className="w-4 h-4" />
                    <span>Profile Analysis</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isContentProducerActive}
                  className={isContentProducerActive ? "bg-muted" : ""}
                >
                  <Link to="/grow/content-producer">
                    <Sparkles className="w-4 h-4" />
                    <span>Content Producer</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton disabled>
                  <MoreHorizontal className="w-4 h-4" />
                  <span className="text-primary/40">
                    More tools coming soon
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Monetization CTA */}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="mb-3">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-lg text-center">
                  💸 Looking to monetize your content?
                </h3>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 text-center">
                  Find brand partnerships through CreatorGrid marketplace
                </p>
              </div>
              <Button
                asChild
                size="sm"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => {
                  track(GROW_EVENTS.MONETIZE_CLICKED, {
                    location: "grow_sidebar",
                    page_path: location.pathname,
                    timestamp: new Date().toISOString(),
                    has_connected_accounts: hasConnectedAccounts,
                    connected_platforms: socialAccounts.map(
                      (acc) => acc.platform,
                    ),
                    total_connected_accounts: socialAccounts.length,
                    selected_account: selectedAccount,
                    selected_platform: socialAccounts.find(
                      (acc) => acc.id === selectedAccount,
                    )?.platform,
                    target_url: `${window.location.origin}/dashboard/partnerships/apply`,
                    interaction_type: "external_link",
                  });
                }}
              >
                <a
                  href={`${window.location.origin}/dashboard/partnerships/apply`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  Get Started
                  <ExternalLink className="w-3 h-3" />
                </a>
              </Button>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};
